"use client"

import React from "react"
import { TrendingUp } from "lucide-react"
import { cn } from "@/app/utils"
import useLocaleStore from "@/app/stores/useLocaleStore"

/**
 * TrendingBadge component props
 */
interface TrendingBadgeProps {
  className?: string
  size?: "sm" | "md" | "lg"
  variant?: "default" | "compact"
}

/**
 * TrendingBadge component
 * Displays a "Trending" badge with animated gradient background
 * 
 * @example
 * <TrendingBadge />
 * // Displays: 🔥 Trending
 * 
 * <TrendingBadge variant="compact" size="sm" />
 * // Displays: 🔥 (compact, small)
 */
export const TrendingBadge: React.FC<TrendingBadgeProps> = ({
  className,
  size = "md",
  variant = "default"
}) => {
  const locale = useLocaleStore((state) => state.locale) as 'en' | 'am'
  
  const label = locale === 'am' ? 'ትሬንዲንግ' : 'Trending'
  
  // Size classes
  const sizeClasses = {
    sm: "text-[10px] px-2 py-0.5 gap-1",
    md: "text-[11px] px-2.5 py-1 gap-1.5",
    lg: "text-xs px-3 py-1.5 gap-2"
  }
  
  const iconSizes = {
    sm: "w-2.5 h-2.5",
    md: "w-3 h-3",
    lg: "w-3.5 h-3.5"
  }
  
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-bold uppercase tracking-wide",
        "bg-gradient-to-r from-orange-500 via-red-500 to-pink-500",
        "text-white shadow-sm",
        "animate-gradient-x", // Custom animation (needs to be defined in tailwind.config.js)
        sizeClasses[size],
        className
      )}
      aria-label="Trending item"
    >
      <TrendingUp className={cn(iconSizes[size], "flex-shrink-0")} aria-hidden="true" />
      {variant === "default" && <span>{label}</span>}
    </span>
  )
}

/**
 * Conditional TrendingBadge
 * Only renders if isTrending is true
 * 
 * @example
 * <ConditionalTrendingBadge isTrending={true} />
 */
export const ConditionalTrendingBadge: React.FC<
  TrendingBadgeProps & { isTrending: boolean }
> = ({ isTrending, ...props }) => {
  if (!isTrending) {
    return null
  }
  
  return <TrendingBadge {...props} />
}
